import { setMaintenancePollingActive } from './maintenancePolling';

const DEFAULT_CHECK_URL = '/up';
const DEFAULT_POLL_INTERVAL_MS = 10000;
const DEFAULT_MAINTENANCE_HTTP_STATUS = 503;
const MIN_VISIBLE_MS_FOR_TOAST = 2000;

let poll_timer = null;
let is_maintenance_active = false;
let abort_controller = null;
let overlay_visible_since = null;
let is_page_unloading = false;

/**
 * Poll /up and show a full-screen overlay while the application is in maintenance mode (HTTP 503).
 */
export function initMaintenanceWatcher(options = {}) {
	const overlay = document.getElementById('maintenance-overlay');

	if (!overlay) {
		return;
	}

	if (overlay.dataset.watcherInitialized === 'true') {
		return;
	}

	overlay.dataset.watcherInitialized = 'true';

	const check_url = options.checkUrl ?? overlay.dataset.checkUrl ?? DEFAULT_CHECK_URL;
	const poll_interval_ms =
		options.pollIntervalMs ?? (Number(overlay.dataset.pollIntervalMs) || DEFAULT_POLL_INTERVAL_MS);
	const maintenance_http_status =
		options.maintenanceHttpStatus ??
		(Number(overlay.dataset.maintenanceHttpStatus) || DEFAULT_MAINTENANCE_HTTP_STATUS);
	const status_el = overlay.querySelector('[data-maintenance-status]');

	function setStatus(message) {
		if (status_el) {
			status_el.textContent = message;
		}
	}

	function enterMaintenanceMode() {
		if (is_maintenance_active) {
			return;
		}

		is_maintenance_active = true;
		setMaintenancePollingActive(true);
	}

	function showOverlay() {
		if (overlay.classList.contains('is-visible') || is_page_unloading) {
			return;
		}

		overlay.hidden = false;
		overlay.setAttribute('aria-hidden', 'false');
		document.body.classList.add('maintenance-overlay-open');

		requestAnimationFrame(() => {
			overlay.classList.add('is-visible');
			overlay_visible_since = Date.now();
		});
	}

	function hideOverlay() {
		const was_visible = overlay.classList.contains('is-visible');
		const was_active = is_maintenance_active;
		const visible_duration =
			overlay_visible_since !== null ? Date.now() - overlay_visible_since : 0;

		overlay.classList.remove('is-visible');
		overlay.hidden = true;
		overlay.setAttribute('aria-hidden', 'true');
		document.body.classList.remove('maintenance-overlay-open');
		is_maintenance_active = false;
		overlay_visible_since = null;
		setMaintenancePollingActive(false);
		setStatus('Verifica del servizio in corso…');

		if (
			was_active &&
			was_visible &&
			visible_duration >= MIN_VISIBLE_MS_FOR_TOAST &&
			typeof toastr !== 'undefined'
		) {
			toastr.info('Il servizio è di nuovo disponibile.');
		}
	}

	function handleMaintenanceResponse() {
		enterMaintenanceMode();
		showOverlay();
		setStatus('Verifica del servizio in corso…');
	}

	function handleAvailableResponse() {
		if (is_maintenance_active) {
			hideOverlay();
		}
	}

	async function checkAvailability() {
		if (document.hidden || is_page_unloading) {
			return;
		}

		abort_controller?.abort();
		abort_controller = new AbortController();

		const separator = check_url.includes('?') ? '&' : '?';
		const url = `${check_url}${separator}_maintenance_check=${Date.now()}`;

		try {
			const response = await fetch(url, {
				method: 'GET',
				cache: 'no-store',
				credentials: 'same-origin',
				headers: {
					Accept: 'application/json',
				},
				signal: abort_controller.signal,
			});

			if (response.status === maintenance_http_status) {
				handleMaintenanceResponse();

				return;
			}

			if (!response.ok) {
				return;
			}

			const payload = await response.json();

			if (payload?.status === 'ok') {
				handleAvailableResponse();
			}
		} catch (error) {
			if (error?.name === 'AbortError') {
				return;
			}
		}
	}

	function startPolling() {
		window.clearInterval(poll_timer);
		poll_timer = window.setInterval(checkAvailability, poll_interval_ms);
	}

	window.addEventListener('pagehide', () => {
		is_page_unloading = true;
	});

	window.addEventListener('pageshow', (event) => {
		if (event.persisted) {
			is_page_unloading = false;
			checkAvailability();
		}
	});

	document.addEventListener('visibilitychange', () => {
		if (!document.hidden && !is_page_unloading) {
			checkAvailability();
		}
	});

	checkAvailability();
	startPolling();
}
